import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import Button from '../common/Button';
import MathCaptcha from '../common/MathCaptcha';
import toast from 'react-hot-toast';

const RegisterForm = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [captchaValid, setCaptchaValid] = useState(false);
    const { register: registerUser, loading, error } = useAuth();
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors }
    } = useForm();

    const password = watch('password');

    // Manejar validación del CAPTCHA
    const handleCaptchaValidation = (isValid) => {
        setCaptchaValid(isValid);
    };

    const onSubmit = async (data) => {
        // Verificar CAPTCHA antes de enviar
        if (!captchaValid) {
            toast.error('Por favor completa la verificación de seguridad');
            return;
        }

        try {
            await registerUser({
                email: data.email,
                full_name: data.full_name,
                password: data.password
                // No enviamos role aquí, el backend lo asignará como 'user' por defecto
            });
            toast.success('¡Registro exitoso! Ya puedes iniciar sesión');
            navigate('/login');
        } catch (err) {
            toast.error(error || 'Error al registrarse');
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <h1 className="text-center text-4xl font-bold text-blue-600 mb-2">ARGUS</h1>
                    <h2 className="text-center text-2xl font-bold text-gray-900">
                        Crear Cuenta
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        ¿Ya tienes cuenta?{' '}
                        <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                            Inicia sesión aquí
                        </Link>
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
                    <div className="bg-white p-8 rounded-lg shadow-md">
                        <div className="space-y-4">
                            <div className="icon-input-container">
                                <User className="input-icon h-5 w-5" />
                                <input
                                    {...register('full_name', {
                                        required: 'El nombre completo es obligatorio',
                                        minLength: {
                                            value: 2,
                                            message: 'El nombre debe tener al menos 2 caracteres'
                                        }
                                    })}
                                    type="text"
                                    placeholder="Tu nombre completo"
                                    className={errors.full_name ? 'input-error' : ''}
                                />
                                {errors.full_name && (
                                    <p className="mt-1 text-sm text-red-600 font-medium">{errors.full_name.message}</p>
                                )}
                            </div>

                            <div className="icon-input-container">
                                <Mail className="input-icon h-5 w-5" />
                                <input
                                    {...register('email', {
                                        required: 'El email es obligatorio',
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: 'Email inválido'
                                        }
                                    })}
                                    type="email"
                                    placeholder="tu@email.com"
                                    className={errors.email ? 'input-error' : ''}
                                />
                                {errors.email && (
                                    <p className="mt-1 text-sm text-red-600 font-medium">{errors.email.message}</p>
                                )}
                            </div>

                            <div className="icon-input-container">
                                <Lock className="input-icon h-5 w-5" />
                                <input
                                    {...register('password', {
                                        required: 'La contraseña es obligatoria',
                                        minLength: {
                                            value: 6,
                                            message: 'La contraseña debe tener al menos 6 caracteres'
                                        }
                                    })}
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Tu contraseña"
                                    className={errors.password ? 'input-error' : ''}
                                    style={{ paddingRight: '48px' }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors z-20"
                                >
                                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                                {errors.password && (
                                    <p className="mt-1 text-sm text-red-600 font-medium">{errors.password.message}</p>
                                )}
                            </div>

                            <div className="icon-input-container">
                                <Lock className="input-icon h-5 w-5" />
                                <input
                                    {...register('confirmPassword', {
                                        required: 'Confirma tu contraseña',
                                        validate: value => value === password || 'Las contraseñas no coinciden'
                                    })}
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Confirmar contraseña"
                                    className={errors.confirmPassword ? 'input-error' : ''}
                                />
                                {errors.confirmPassword && (
                                    <p className="mt-1 text-sm text-red-600 font-medium">{errors.confirmPassword.message}</p>
                                )}
                            </div>

                            {/* CAPTCHA */}
                            <div className="border-t pt-4">
                                <MathCaptcha onValidation={handleCaptchaValidation} />
                            </div>
                        </div>

                        <div className="mt-6">
                            <Button
                                type="submit"
                                loading={loading}
                                disabled={!captchaValid}
                                className={`w-full ${!captchaValid ? 'opacity-50 cursor-not-allowed' : ''}`}
                                size="lg"
                            >
                                Crear Cuenta
                            </Button>

                            {!captchaValid && (
                                <p className="mt-2 text-xs text-gray-500 text-center">
                                    Completa la verificación de seguridad para continuar
                                </p>
                            )}

                            {/* Nota sobre el tipo de cuenta */}
                            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-xs text-blue-700 text-center">
                                    ℹ️ Se creará una cuenta de usuario normal. Para permisos de administrador, contacta a un administrador existente.
                                </p>
                            </div>
                        </div>

                        {error && (
                            <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                                <p className="font-medium">{error}</p>
                            </div>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RegisterForm;